import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AI_GENERATE_TYPE,
  AI_GENERATION_STAGE,
  AIMedia,
  AIMediaStatus,
  AudioCredit,
  Status,
  WebSocketDataReturnType,
} from '../../@types';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { CreditContext } from '../../context/CreditProvider';
import {
  useFetchRequiredCredits,
  useGenerateAiMedia,
  useGetAiGenerationStatus,
  usePresignedUrlUpload,
  useUploadId,
} from '../../services';
import { useRouter } from 'next/router';
import { useAppContext } from '../context';
import {
  AiVideoModelEnum,
  API_CONSTANTS,
  getRandomInt,
  IMAGE_DEFAULT_NAME_PREFIX,
  isCreditsAvailable,
  isInputEmpty,
  NumGenerationsEnum,
  ResolutionEnum,
  TEXT_TO_SPEECH_MAX_NAME_LENGTH,
  VIDEO_DEFAULT_NAME_PREFIX,
} from '../../utils';
import { useHandleApiCall } from '../useHandleApiCall';
import { generatePreSignedUrl } from '../../../t2c-fe/services/upload/generatePreSignedUrl';

const getDialogTitle = (currentStage: AI_GENERATION_STAGE, isImage: boolean): string => {
  switch (currentStage) {
    case AI_GENERATION_STAGE.PROMPT_INPUT:
      return isImage ? 'Generate AI Image' : 'Generate AI Video';

    case AI_GENERATION_STAGE.CREDITS_CHECK:
      return isImage ? 'Confirm Generate AI Image' : 'Confirm Generate AI Video';

    case AI_GENERATION_STAGE.GENERATING:
      return isImage ? 'Generating AI Image' : 'Generating AI Video';

    case AI_GENERATION_STAGE.CANCEL_CONFIRMATION:
      return 'Cancel AI Video Generation';

    case AI_GENERATION_STAGE.VIDEO_PREVIEW:
      return isImage ? 'Image Preview' : 'Video Preview';

    case AI_GENERATION_STAGE.ERROR:
      return isImage ? 'Error Generating Image' : 'Error Generating Video';

    default:
      return '';
  }
};

interface Props {
  isImage: boolean;
  handleApplyBroll: (mediaUrl?: string, thumbnailUrl?: string) => void;
  handleOnCloseDialog: () => void;
  fetchAiMediaData: () => void;
  shouldOpenDialog: boolean;
}

export const useAiGenerate = ({
  isImage,
  handleApplyBroll,
  handleOnCloseDialog,
  fetchAiMediaData,
  shouldOpenDialog,
}: Props) => {
  const { modelDetails, handleUpdateUserCredit } = useContext(CreditContext);
  const [currentStage, setCurrentStage] = useState<AI_GENERATION_STAGE>(
    AI_GENERATION_STAGE.PROMPT_INPUT
  );
  const [prompt, setPrompt] = useState<string>('');

  const defaultMediaName =
    (isImage ? IMAGE_DEFAULT_NAME_PREFIX : VIDEO_DEFAULT_NAME_PREFIX) + getRandomInt(1, 1000);

  const [clipGenerationData, setClipGenerationData] = useState({
    uploadId: '',
    uploadTailId: '',
    duration: modelDetails[AiVideoModelEnum.VEO3].durationPerModel[0],
    numGenerations: parseInt(NumGenerationsEnum.ONE),
    orientation: modelDetails[AiVideoModelEnum.VEO3].orientationPerModel[0],
    resolution: ResolutionEnum['720P'],
    model: AiVideoModelEnum.VEO3,
  });

  const [mediaName, setMediaName] = useState<string>(defaultMediaName);
  const [genAiMediaData, setGenAiMediaData] = useState<AIMedia | null>(null);
  const [progress, setProgress] = useState(0);
  const abortController = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadURL, setUploadURL] = useState('');
  const [uploadTailURL, setUploadTailURL] = useState('');
  const [isSelectedTail, setIsSelectedTail] = useState(false);
  const [maxCharLimit, setMaxCharLimit] = useState<number>(
    modelDetails[AiVideoModelEnum.VEO3].maxCharLimit
  );
  const { patch } = useHandleApiCall();
  const updateUserMutation = patch(API_CONSTANTS.UPDATE_USER.key, API_CONSTANTS.UPDATE_USER.path);
  const [checked, setChecked] = useState(false);
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);
  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const updateData = { showCreditDialog: !event.target.checked };
      const res = await updateUserMutation.mutateAsync(updateData);
      setChecked(!res.showCreditDialog);
      setShowCreditDialog(res.showCreditDialog);
    } catch (error) {
      console.log(error);
    }
  };

  const { enqueueSnackbar } = useSnackbar();

  const { push } = useRouter();
  const { websocket, showCreditDialog, setShowCreditDialog } = useAppContext();
  const { isConnected, aiGenerateTaskMap, sendMessage, handleRemoveFromTaskMap } = websocket;

  const { mutateAsync: mutateGenerateAiMedia } = useGenerateAiMedia();

  const FormSchema = Yup.object().shape({
    prompt: Yup.string()
      .required('Prompt is required')
      .test('not-empty', 'Prompt is required', (value) => !isInputEmpty(value))
      .typeError('Prompt must be a valid string'),
    name: Yup.string()
      .required('Name is required')
      .test('not-empty', 'Name is required', (value) => !isInputEmpty(value))
      .max(
        TEXT_TO_SPEECH_MAX_NAME_LENGTH,
        `Name must be at most ${TEXT_TO_SPEECH_MAX_NAME_LENGTH} characters`
      )
      .typeError('Name must be a valid string'),
    referenceFile: Yup.mixed().nullable(), // Optional field
    duration: Yup.number().default(5),
  });

  const formMethods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues: { prompt: '', name: '' },
    mode: 'onChange',
  });

  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    isError: isCreditFetchError,
  } = useFetchRequiredCredits();

  const handleStageTransition = (nextStage: AI_GENERATION_STAGE) => {
    setCurrentStage(nextStage);
  };

  const handlePromptChange = (newPrompt: string) => setPrompt(newPrompt);
  const handleMediaNameChange = (newPrompt: string) => setMediaName(newPrompt);

  const handleClipDataChange = (key: string, value: string) => {
    if (value && (key === 'duration' || key === 'numGenerations'))
      setClipGenerationData({ ...clipGenerationData, [key]: parseInt(value) });
    else setClipGenerationData({ ...clipGenerationData, [key]: value });
  };

  const handleImageSelect = async (url: string) => {
    const pathname = new URL(url).pathname;
    const parts = pathname.split('/');
    const imageId = parts[parts.length - 1];
    if (isSelectedTail) {
      setClipGenerationData({ ...clipGenerationData, uploadTailId: imageId });
      setUploadTailURL(url);
    } else {
      setClipGenerationData({ ...clipGenerationData, uploadId: imageId });
      setUploadURL(url);
    }
  };

  const handleDeselectImage = () => {
    setUploadURL('');
    setClipGenerationData({ ...clipGenerationData, uploadId: '' });
  };

  const handleDeselectTailImage = () => {
    setUploadTailURL('');
    setClipGenerationData({ ...clipGenerationData, uploadTailId: '' });
  };

  const validateFormData = () => {
    if (!prompt.trim()) {
      enqueueSnackbar('Prompt cannot be empty.', { variant: 'error' });
      return false;
    }

    if (!mediaName.trim()) {
      enqueueSnackbar('Name cannot be empty.', { variant: 'error' });
      return false;
    }

    return true;
  };

  const handleGenerateClick = async () => {
    if (validateFormData()) {
      // handleStageTransition(AI_GENERATION_STAGE.CREDITS_CHECK);
      const res = await mutateFetchRequiredCredits({
        aiGenType: isImage ? 'image' : 'video',
        text: prompt,
        ...(isImage && { numGenerations: 1 }),
        ...(!isImage && {
          duration: clipGenerationData.duration,
          numGenerations: clipGenerationData.numGenerations,
          model: clipGenerationData.model,
        }),
      });
      setCreditsData(res);
      handleUpdateUserCredit();
    }
  };

  useEffect(() => {
    if (creditsData) {
      const isEnoughCredit = isCreditsAvailable(
        creditsData?.availableCredits,
        creditsData?.creditsInUse,
        creditsData?.requiredCredits
      );
      if (showCreditDialog) {
        handleStageTransition(AI_GENERATION_STAGE.CREDITS_CHECK);
      } else {
        if (!isFetchingRequiredCredits && isEnoughCredit) {
          handleOnConfirmGeneration();
        } else if (!isEnoughCredit) {
          handleRedirectToPricingPage();
        }
      }
    }
  }, [creditsData]);

  const handleResolutionvalue = (value: ResolutionEnum) => {
    return value === ResolutionEnum['720P']
      ? 720
      : value === ResolutionEnum['480P']
      ? 480
      : value === ResolutionEnum['360P']
      ? 360
      : 240;
  };

  const handleOnConfirmGeneration = async () => {
    handleStageTransition(AI_GENERATION_STAGE.GENERATING);
    try {
      const requests = Array.from({ length: clipGenerationData.numGenerations }, () =>
        mutateGenerateAiMedia({
          category: isImage ? 'image' : 'video',
          prompt,
          name: mediaName,
          ...(!isImage && {
            uploadId: clipGenerationData.uploadId,
            uploadTailId: clipGenerationData.uploadTailId,
            duration: clipGenerationData.duration,
            orientation: clipGenerationData.orientation,
            resolution: handleResolutionvalue(clipGenerationData.resolution),
            model: clipGenerationData.model,
          }),
        })
      );
      const res = await Promise.all(requests);
      setGenAiMediaData(res[0]);
      if (isImage) {
        handleStageTransition(AI_GENERATION_STAGE.VIDEO_PREVIEW);
        fetchAiMediaData();
        handleUpdateUserCredit();
      } else {
        sendMessage({
          event: 'register',
          data: {
            id: res[0]?.id,
          },
        });
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate video. Please try again.', { variant: 'error' });
      handleStageTransition(AI_GENERATION_STAGE.ERROR);
    }
  };

  const handleMinimizeClick = () => {
    enqueueSnackbar('Generation process minimized.', { variant: 'info' });
    handleCloseAiGenDialog();
    fetchAiMediaData();
  };

  const handleContinueGeneration = () => {
    handleStageTransition(AI_GENERATION_STAGE.GENERATING);
  };

  const resetToPromptInput = () => {
    setPrompt('');
    setMediaName(defaultMediaName);
    setGenAiMediaData(null);
    handleStageTransition(AI_GENERATION_STAGE.PROMPT_INPUT);
    setProgress(0);
  };

  const handleRedirectToPricingPage = () => {
    push('/subscription?from=edit-video');
  };

  const handleReGenerateClick = () => {
    setGenAiMediaData(null);
    handleStageTransition(AI_GENERATION_STAGE.PROMPT_INPUT);
  };

  const handleOnApply = () => {
    handleApplyBroll(genAiMediaData?.url, genAiMediaData?.thumbnailUrl);
    enqueueSnackbar(`${isImage ? 'Image' : 'Video'} applied successfully.`, { variant: 'success' });
  };

  const handleCloseAiGenDialog = () => {
    resetToPromptInput();
    handleOnCloseDialog();
  };

  const dialogTitle = getDialogTitle(currentStage, isImage);

  useEffect(() => {
    if (!genAiMediaData?.id) return;

    const resData = aiGenerateTaskMap.get(genAiMediaData?.id);
    if (!resData) return;

    setProgress(resData?.progress || 0);

    if (resData.status === Status.Success) {
      setGenAiMediaData(resData.data?.aiMediaData);
      handleStageTransition(AI_GENERATION_STAGE.VIDEO_PREVIEW);
      fetchAiMediaData();
      handleUpdateUserCredit();
      handleRemoveFromTaskMap({ id: genAiMediaData.id, type: WebSocketDataReturnType.AI_GENERATE });
    } else if (resData.status === Status.Error) {
      enqueueSnackbar('Generation failed', { variant: 'error' });
      fetchAiMediaData();
      handleStageTransition(AI_GENERATION_STAGE.ERROR);
      handleUpdateUserCredit();
      handleRemoveFromTaskMap({ id: genAiMediaData.id, type: WebSocketDataReturnType.AI_GENERATE });
    } else {
      setGenAiMediaData(() => {
        return resData?.data?.aiMediaData
          ? resData.data?.aiMediaData
          : {
              ...genAiMediaData,
              status: (resData?.status || AIMediaStatus.InProgress) as AIMediaStatus,
            };
      });
    }
  }, [aiGenerateTaskMap.get(genAiMediaData?.id)]);

  useEffect(() => {
    if (!shouldOpenDialog) {
      handleCloseAiGenDialog();
    }
  }, [shouldOpenDialog]);

  return {
    currentStage,
    prompt,
    mediaName,
    clipGenerationData,
    setClipGenerationData,
    handleClipDataChange,
    handlePromptChange,
    handleGenerateClick,
    handleMinimizeClick,
    handleContinueGeneration,
    handleReGenerateClick,
    handleOnApply,
    handleCloseAiGenDialog,
    handleMediaNameChange,
    uploading,
    uploadURL,
    setUploadURL,
    handleDeselectImage,
    isFetchingRequiredCredits,
    requiredCreditsData,
    dialogTitle,
    genAiMediaData,
    handleOnConfirmGeneration,
    handleRedirectToPricingPage,
    checked,
    handleCheckboxChange,
    usableCredits: requiredCreditsData?.availableCredits - requiredCreditsData?.creditsInUse,
    isCreditFetchError,
    progress,
    formMethods,
    setUploadTailURL,
    handleDeselectTailImage,
    handleImageSelect,
    uploadTailURL,
    setIsSelectedTail,
  };
};
